import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { createFilterOptions } from "@mui/material";
import type { CommuneOuCommuneAssocieeDeleguee } from "models";
import { communesService, communesAssocieesDelegueesService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { MultiAutoComplete } from "./MultiAutoComplete";
import { grey } from "@mui/material/colors";
import { multiAutoClassNames, sortingUtil } from "utils";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: CommuneOuCommuneAssocieeDeleguee) => {
    const normalizedString = stringUtil.normalize(option.libelle);
    return `${normalizedString} ${option.libelle}`;
  },
});

function getCommuneOuCommuneAssocieeDelegueeKey(
  communeOuCad: CommuneOuCommuneAssocieeDeleguee
): string {
  return `${communeOuCad.id}-${communeOuCad._type}`;
}

interface CommuneOuCommuneAssocieeDelegueeMultiAutoProps {
  value: Array<CommuneOuCommuneAssocieeDeleguee>;
  onChange?: (communeOuCad: Array<CommuneOuCommuneAssocieeDeleguee>) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
}

function CommuneOuCommuneAssocieeDelegueeMultiAuto({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
}: Readonly<CommuneOuCommuneAssocieeDelegueeMultiAutoProps>): ReactNode {
  const [communesOuCads, setCommunesOuCads] = useState<Array<CommuneOuCommuneAssocieeDeleguee>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getCommunesOuCads(): Promise<void> {
      const [_communes, _communesAssocieesDeleguees] = await Promise.all([
        communesService.getAll(),
        communesAssocieesDelegueesService.getAll(),
      ]);
      const _communesOuCads = [..._communes, ..._communesAssocieesDeleguees];
      _communesOuCads.sort(sortingUtil.sortCommunes);
      setCommunesOuCads(_communesOuCads);
    }
    void catchErrors(getCommunesOuCads);
  }, [catchErrors]);

  return (
    <MultiAutoComplete
      value={value}
      onChange={onChange}
      options={communesOuCads}
      filterOptions={filterOptions}
      size={size}
      singleItemLabel="Commune"
      isLoading={isLoading}
      label={label}
      placeholder={placeholder}
      renderOption={(props, communeOuCad, { selected }) => (
        <li {...props} key={getCommuneOuCommuneAssocieeDelegueeKey(communeOuCad)}>
          <div
            className={`${multiAutoClassNames.AUTOCOMPLETE_CSS_CHECKBOX} ${selected ? multiAutoClassNames.CHECKED : multiAutoClassNames.UNCHECKED}`}
          />
          {communeOuCad._type === "commune-associee-deleguee" ? (
            <i>{communeOuCad.libelle}</i>
          ) : (
            communeOuCad.libelle
          )}{" "}
          <span className="grey">({communeOuCad.id.substring(0, 2)})</span>
        </li>
      )}
      getOptionKey={getCommuneOuCommuneAssocieeDelegueeKey}
      isOptionEqualToValue={(o, v) =>
        getCommuneOuCommuneAssocieeDelegueeKey(o) === getCommuneOuCommuneAssocieeDelegueeKey(v)
      }
      slotProps={{
        paper: { sx: { "&.MuiAutocomplete-paper li span.grey": { color: grey[500], ml: 0.5 } } },
      }}
    />
  );
}

export const CommuneOuCommuneAssocieeDelegueeMultiAutoWithErrorBoundary =
  withComponentErrorBoundary(CommuneOuCommuneAssocieeDelegueeMultiAuto);
